import { Box, Button, useTheme, Stack, IconButton } from '@mui/material';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import Form from 'components/Form';
import { QuillEditor } from 'components/QuillEditor';
import useI18n from 'hooks/useI18n';
import useNotification from 'hooks/useNotification';
import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { Close } from '@mui/icons-material';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import useUser from 'store/user/useUser';
import {
  isRichHtml,
  normalizePlainText,
} from 'utils/medicalNoteContent';
import { postUploadFile } from 'routes/StudentManagement/DisplayInformation/AdminList/PatientUpdate/etc';

declare const PuzzleSDK: any;

const MAX_NARRATIVE_IMAGE_SIZE_MB = 5;

const htmlToPlainTextForVoice = (html: string): string => {
  const raw = html ?? '';
  if (!raw.trim()) return '';
  if (!isRichHtml(raw)) return normalizePlainText(raw);

  const doc = new DOMParser().parseFromString(raw, 'text/html');
  return normalizePlainText(doc.body.innerText ?? '');
};

interface Props {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  editorResetKey?: number | string;
  disabled?: boolean;
  isTestMode?: boolean;
}

export interface NarrativeRecordHandle {
  clearDraft: () => void;
  /** 간호기록 수정 시 reset() 이후 Quill에 내용을 다시 채움 (content 생략 시 폼 값 사용) */
  reloadEditor: (content?: string) => void;
}

const NarrativeRecord = forwardRef<NarrativeRecordHandle, Props>(
  (
    { register, setValue, watch, editorResetKey = 'new', disabled, isTestMode },
    ref,
  ) => {
    const i18n = useI18n();
    const { palette } = useTheme();
    const { onFail, onResultCode, onSuccess } = useNotification();
    const [voiceOn, setVoiceOn] = useState<boolean>(false);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [narrativeEditorKey, setNarrativeEditorKey] = useState(0);
    const puzzleWsRef = useRef<any>(null);
    const voiceTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const { student_id } = useUser();

    register('narrativeNote');

    const DRAFT_KEY = `narrative_draft_${student_id}`;

    const bumpNarrativeEditor = useCallback(() => {
      setNarrativeEditorKey(key => key + 1);
    }, []);

    const syncVoiceTextarea = useCallback((html: string) => {
      if (voiceTextareaRef.current) {
        voiceTextareaRef.current.value = htmlToPlainTextForVoice(html);
      }
    }, []);

    const reloadEditor = useCallback(
      (content?: string) => {
        const html =
          content !== undefined ? content : watch('narrativeNote') ?? '';
        if (content !== undefined) {
          setValue('narrativeNote', content, { shouldValidate: true });
        }
        syncVoiceTextarea(html);
        bumpNarrativeEditor();
      },
      [setValue, syncVoiceTextarea, watch],
    );

    useImperativeHandle(ref, () => ({
      clearDraft: () => {
        sessionStorage.removeItem(DRAFT_KEY);
        setValue('narrativeNote', '', { shouldValidate: true });
        syncVoiceTextarea('');
        bumpNarrativeEditor();
      },
      reloadEditor,
    }));

    useEffect(() => {
      if (editorResetKey !== 'new') return;

      const draft = sessionStorage.getItem(DRAFT_KEY);
      if (draft) {
        setValue('narrativeNote', draft, { shouldValidate: true });
        syncVoiceTextarea(draft);
        bumpNarrativeEditor();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNarrativeNoteChange = useCallback(
      (html: string) => {
        setValue('narrativeNote', html, { shouldValidate: true });
        sessionStorage.setItem(DRAFT_KEY, html);
        syncVoiceTextarea(html);
      },
      [DRAFT_KEY, setValue, syncVoiceTextarea],
    );

    const handleNarrativeImageUpload = useCallback(
      async (file: File): Promise<string | null> => {
        if (file.size > MAX_NARRATIVE_IMAGE_SIZE_MB * 1024 * 1024) {
          onFail(
            '파일업로드 실패하였습니다',
            `업로드 가능한 용량을 초과하였습니다.(최대 ${MAX_NARRATIVE_IMAGE_SIZE_MB}MB)`,
          );
          return null;
        }

        try {
          const {
            data: { rc, bucket_url },
          } = await postUploadFile({ file });

          if (rc !== 1) {
            onResultCode(rc);
            return null;
          }

          onSuccess('파일업로드 성공하였습니다.');
          return bucket_url;
        } catch (error) {
          onFail('파일업로드 실패하였습니다', error);
          return null;
        }
      },
      [onFail, onResultCode, onSuccess],
    );

    const handleNarrativeVideoError = useCallback(
      (message: string) => {
        onFail(message, null);
      },
      [onFail],
    );

    useEffect(() => {
      const interval = setInterval(() => {
        if (!voiceTextareaRef.current) return;

        const voiceValue = voiceTextareaRef.current.value;
        const formValue = watch('narrativeNote') ?? '';
        const formPlain = htmlToPlainTextForVoice(formValue);

        if (voiceValue !== formPlain) {
          // 음성 필드가 비어 있을 때 기존 HTML 서술기록을 plain으로 덮어쓰지 않음 (수정 로드 직후 레이스 방지)
          if (!voiceValue.trim() && isRichHtml(formValue)) return;

          setValue('narrativeNote', voiceValue, { shouldValidate: true });
          sessionStorage.setItem(DRAFT_KEY, voiceValue);
          bumpNarrativeEditor();
        }
      }, 100);

      return () => clearInterval(interval);
    }, [DRAFT_KEY, bumpNarrativeEditor, setValue, watch]);

    const sttCallback = (stt: string) => {
      if (!stt) return;
    };

    const connectedCallback = (status: boolean) => {
      console.log('connectedCallback:', status);
      setIsConnected(status);
    };

    const initializePuzzleSDK = () => {
      try {
        if (!PuzzleSDK) {
          console.error('PuzzleSDK is not loaded');
          return false;
        }

        const uri = process.env.REACT_APP_PUZZLE_AI_WS_URL;
        const appKey = process.env.REACT_APP_PUZZLE_AI_APP_KEY;
        const userId = student_id;
        const modelLanguage = 'KOR';
        const channels = 1;
        const isMonoModeInStereoModel = false;

        const fullUri = `${uri}?appKey=${encodeURIComponent(
          appKey || '',
        )}&userId=${encodeURIComponent(
          userId || '',
        )}&modelLanguage=${encodeURIComponent(
          modelLanguage,
        )}&channels=${encodeURIComponent(
          channels.toString(),
        )}&isMonoModeInStereoModel=${encodeURIComponent(
          isMonoModeInStereoModel.toString(),
        )}`;

        PuzzleSDK.createInstance(fullUri, connectedCallback, sttCallback);

        return true;
      } catch (error) {
        console.error('Failed to initialize PuzzleSDK:', error);
        return false;
      }
    };

    const handleVoiceConnect = () => {
      try {
        if (!puzzleWsRef.current) {
          const initialized = initializePuzzleSDK();
          if (!initialized) return;

          puzzleWsRef.current = PuzzleSDK.getInstance();
          puzzleWsRef.current.addBoard('narrative', '서술기록(음성AI)');
          puzzleWsRef.current.addItem('narrative', 'narrativeNote', '서술');
        }

        puzzleWsRef.current.restartRecord();
        setIsConnected(true);
      } catch (error) {
        console.error('Failed to connect:', error);
        setIsConnected(false);
      }
    };

    const handleVoiceDisconnect = () => {
      try {
        if (puzzleWsRef.current) {
          puzzleWsRef.current.stopRecord();
          setIsConnected(false);

          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to disconnect:', error);
      }
    };

    const handleVoiceRestart = () => {
      try {
        if (puzzleWsRef.current) {
          puzzleWsRef.current.stopRecord();
          setTimeout(() => {
            puzzleWsRef.current.restartRecord();
          }, 200);
        }
      } catch (error) {
        console.error('Failed to restart:', error);
      }
    };

    const VoiceMenuOnHandler = () => {
      setVoiceOn(true);
    };

    const VoiceMenuOFFHandler = () => {
      handleVoiceDisconnect();
      setVoiceOn(false);
    };

    const openManual = () => {
      window.open(
        'https://smartnurse.notion.site/Voice-EMR-ce65ddfe10784609a51ffbbd564ad635',
        '_blank',
      );
    };

    const handlePreventEvent = (e: React.ClipboardEvent | React.MouseEvent) => {
      if (isTestMode) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    useEffect(() => {
      const UNLOAD_FLAG_KEY = `${DRAFT_KEY}_unload_flag`;

      const handleBeforeUnload = () => {
        sessionStorage.setItem(UNLOAD_FLAG_KEY, 'true');
      };

      const handleLoad = () => {
        const wasUnload = sessionStorage.getItem(UNLOAD_FLAG_KEY);
        if (wasUnload === 'true') {
          sessionStorage.removeItem(UNLOAD_FLAG_KEY);
        }
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          const wasUnload = sessionStorage.getItem(UNLOAD_FLAG_KEY);
          if (wasUnload === 'true') {
            setTimeout(() => {
              if (document.visibilityState === 'hidden') {
                sessionStorage.removeItem(DRAFT_KEY);
                sessionStorage.removeItem(UNLOAD_FLAG_KEY);
              }
            }, 100);
          }
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('load', handleLoad);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      handleLoad();

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('load', handleLoad);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
        if (puzzleWsRef.current) {
          handleVoiceDisconnect();
        }
      };
    }, [DRAFT_KEY]);

    const narrativeNote = watch('narrativeNote') ?? '';

    return (
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Button
            variant="contained"
            sx={{ minWidth: '15px', padding: '6px 7px' }}
            onClick={VoiceMenuOnHandler}
            disabled={disabled}
          >
            <SettingsVoiceIcon
              fontSize="small"
              sx={{
                color: 'white',
                height: '18px',
                animation: isConnected ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          </Button>
          {!voiceOn ? null : (
            <Box
              sx={{
                border: `1px solid ${palette.primary.main}`,
                borderRadius: '4px',
                marginLeft: '10px',
                display: 'flex',
                alignItems: 'center',
                height: '30px',
              }}
            >
              <Button
                sx={{ fontSize: '12px' }}
                onClick={handleVoiceConnect}
                disabled={isConnected}
              >
                VOICE 연결
              </Button>
              <Button
                sx={{ fontSize: '12px' }}
                onClick={handleVoiceRestart}
                disabled={!isConnected}
              >
                재시작
              </Button>
              <Button
                sx={{ fontSize: '12px' }}
                onClick={handleVoiceDisconnect}
                disabled={!isConnected}
              >
                연결끊기
              </Button>
              <Button sx={{ fontSize: '12px' }} onClick={openManual}>
                사용설명서
              </Button>
              <a
                href="https://puzzle-ai.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{ fontSize: '12px' }}>
                  <span style={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                    Powered by
                  </span>
                  <span style={{ fontWeight: 'bold' }}> PuzzleAI</span>
                </div>
              </a>

              <IconButton size="small" onClick={VoiceMenuOFFHandler}>
                <Close color="primary" sx={{ height: '18px' }} />
              </IconButton>
            </Box>
          )}
        </Box>
        <Form.Item label={i18n('NARRATIVE.NARRATIVENOTE')}>
          <textarea
            id="narrativeNote"
            ref={voiceTextareaRef}
            defaultValue=""
            tabIndex={-1}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          />
          <Box
            onPaste={handlePreventEvent}
            onCopy={handlePreventEvent}
            onContextMenu={handlePreventEvent}
          >
            <QuillEditor
              key={`${narrativeEditorKey}-${editorResetKey}`}
              defaultValue={narrativeNote}
              onChange={handleNarrativeNoteChange}
              onImageUpload={handleNarrativeImageUpload}
              onVideoError={handleNarrativeVideoError}
              placeholder="서술기록 내용을 입력해주세요."
              disabled={disabled}
            />
          </Box>
        </Form.Item>
      </Stack>
    );
  },
);

NarrativeRecord.displayName = 'NarrativeRecord';

export default NarrativeRecord;
